import React from "react";
import { bandFor } from "../StationPerformance.consts";

interface ChangePercentChipProps {
  percent: number | null | undefined;
  /** Mirrors the sign where a rise is the bad news. */
  invert?: boolean;
  /** Appended to the tooltip, for callers that want to name what moved. */
  hint?: string;
  size?: "normal" | "small";
}

/**
 * A percentage change, tinted by which band it falls in.
 *
 * Shared by the station list and the watchlist so the same number is the same colour in both places. A
 * reader who learns the scale on one screen should not have to learn it again on the next.
 *
 * A null percentage renders as words with no colour. Painting it would hand over a confident signal
 * about a number that does not exist.
 */
const ChangePercentChip: React.FC<ChangePercentChipProps> = ({
  percent,
  invert = false,
  hint,
  size = "normal",
}) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) {
    return (
      <span className="text-color-secondary" title="No comparable figure on one side of the window">
        not comparable
      </span>
    );
  }

  const band = bandFor(percent, invert);
  const icon = percent === 0 ? "pi pi-minus" : percent > 0 ? "pi pi-arrow-up" : "pi pi-arrow-down";
  const scale = size === "small" ? "text-xs px-1" : "px-2 py-1";

  return (
    <span
      className={`inline-flex align-items-center border-round font-medium ${scale}`}
      style={{ background: band.background, color: band.text }}
      title={`${percent.toFixed(1)}% ${band.label}${hint ? `. ${hint}` : ""}`}
    >
      <i className={`${icon} mr-1 text-xs`} />
      {percent > 0 ? "+" : ""}
      {percent.toFixed(1)}%
    </span>
  );
};

export default ChangePercentChip;
